<script setup lang="ts">
import { useUploadLicense } from "~/modules/license/composables/useUploadLicense";
import { LICENSE_FEATURE_GAUGES } from "~/modules/license/constants";
import type { License } from "~/modules/license/models/License.interface";
import type { LicenseFeature } from "~/modules/license/models/LicenseFeature.interface";
import type { LicenseFeatureName } from "~/modules/license/models/LicenseFeatureName.enum";
import type { LicenseResourceUsage } from "~/modules/license/models/LicenseResourceUsage.interface";
import { LicenseUploadContext } from "~/modules/license/models/LicenseUploadContext.enum";
import ILxcPlus from "~icons/lxc/plus";

const props = defineProps<{
  organizationCode?: number;
  license: License;
}>();

defineEmits(["uploadSuccess", "uploadError"]);

const { t } = useI18n();
const { canUploadLicense } = useUploadLicense(props.organizationCode);

const tooltipDeviceWarning = "license.gauge.device.warning.tooltip";
const tooltipDeviceError = "license.gauge.device.error.tooltip";

const showUploadModal = ref(false);

const onShowUploadModal = () => {
  showUploadModal.value = true;
};

/**
 * Extract the features to display as gauge
 * Only manage features which are defined in LICENSE_FEATURE_GAUGES and which have positive limits
 */
const licenseFeatures = computed(() =>
  props.license.features
    ? props.license.features.reduce(
        (acc: LicenseResourceUsage[], currentValue: LicenseFeature) => {
          if (
            currentValue.enabled &&
            LICENSE_FEATURE_GAUGES.includes(
              currentValue.name as LicenseFeatureName,
            ) &&
            currentValue.limits &&
            currentValue.limits?.length > 0 &&
            currentValue.limits.some((limit) => {
              return limit.value && limit.value > 0;
            })
          ) {
            acc.push({
              name: currentValue.name,
              total: currentValue.total || 0,
              contracted: currentValue.limits[0].value,
            });
          }
          return acc;
        },
        [],
      )
    : [],
);
</script>

<template>
  <div class="border border-gray-300 rounded-lg w-full">
    <div class="border-b border-gray-300 flex">
      <div
        class="basis-2/3 px-5 py-3.5 border-r border-gray-300 text-gray-700 font-medium"
      >
        {{ license.name }}
      </div>
      <div class="basis-1/3 flex items-center pl-7 py-3.5">
        <license-content-properties-status :license="license" />
      </div>
    </div>
    <div class="flex w-full">
      <!-- left bloc -->
      <div class="basis-2/3 border-r border-gray-300 px-4 py-4 text-gray-700">
        <template v-if="license.maxDevices">
          <license-content-properties-row-title :title="t('license.total')" />

          <license-content-properties-gauge
            :criteria="license.maxDevices"
            :current-number="license.maxDevices.available"
            :show-maximum="true"
          >
            <template #warning>
              <license-content-properties-warning
                :tooltip="t(tooltipDeviceWarning)"
              />
            </template>
            <template #error>
              <license-content-properties-error
                :tooltip="t(tooltipDeviceError)"
              />
            </template>
          </license-content-properties-gauge>
        </template>

        <ul v-else-if="license.deviceTypes && license.deviceTypes?.length > 0">
          <li class="list-none">
            <license-content-properties-row-title
              :title="t('license.deviceTypes.title')"
            />
          </li>
          <li
            v-for="criteria in license.deviceTypes"
            :key="criteria.name"
            class="list-none mb-2"
          >
            <!-- do not display the criteria if contracted = 0 -->
            <license-content-properties-gauge
              v-if="criteria.contracted !== 0"
              :criteria="criteria"
              :current-number="criteria.available"
              :name="criteria.friendlyName || criteria.name"
            >
              <template #warning>
                <license-content-properties-warning
                  :tooltip="t(tooltipDeviceWarning)"
                />
              </template>
              <template #error>
                <license-content-properties-error
                  :tooltip="t(tooltipDeviceError)"
                />
              </template>
            </license-content-properties-gauge>
          </li>
        </ul>

        <ul v-if="licenseFeatures?.length > 0" class="mt-6">
          <li class="list-none">
            <license-content-properties-row-title
              :title="t('license.features.title')"
            />
          </li>
          <li
            v-for="feature in licenseFeatures"
            :key="feature.name"
            class="list-none mb-2"
          >
            <license-content-properties-gauge
              :criteria="feature"
              :current-number="feature.total"
              :name="t('license.features.' + feature.name)"
            >
              <template #warning>
                <license-content-properties-warning
                  :tooltip="t('license.features.warning.tooltip')"
                />
              </template>
              <template #error>
                <license-content-properties-error
                  :tooltip="t('license.features.error.tooltip')"
                />
              </template>
            </license-content-properties-gauge>
          </li>
        </ul>
      </div>

      <!-- right bloc-->
      <div
        class="basis-1/3 pl-14 pr-4 pt-5 pb-4 relative leading-6 text-gray-700 flex flex-col"
      >
        <license-content-properties-characteristics :license="license" />

        <lxc-button
          v-if="canUploadLicense()"
          type="tertiary"
          :title="t('license.upload.new.text')"
          :icon="ILxcPlus"
          class="mt-16 self-end"
          @click="onShowUploadModal"
        >
          {{ t("license.upload.new.text") }}
        </lxc-button>
      </div>
    </div>
  </div>

  <license-upload-modal
    v-model:show="showUploadModal"
    :organization-code="organizationCode"
    :upload-context="LicenseUploadContext.LICENSE_PAGE"
    @success="$emit('uploadSuccess')"
    @error="$emit('uploadError', $event)"
  />
</template>
